import apiClient, { BASE_URL } from "./axios";

export { BASE_URL };

function parseBody(body) {
  if (!body) return undefined;
  return typeof body === "string" ? JSON.parse(body) : body;
}

// Known backend validation messages (see AntiFarmingService) rewritten into
// full, friendly sentences for regular users. Anything not listed here just
// has its technical path/status suffix stripped (see humanizeApiError),
// so new backend messages still display reasonably without a code change.
const FRIENDLY_MESSAGES = [
  [/^too short$/i, "Your post is too short. Please add a bit more detail before submitting."],
  [/^too few words$/i, "Your post needs a few more words before it can be submitted."],
  [/^comment too short$/i, "Your comment is too short. Please write a little more."],
  [/^invalid content$/i, "That doesn't look like valid text. Please rewrite your post."],
  [/^low quality content$/i, "This post looks repetitive or low effort. Please rewrite it with more original wording."],
  [/^duplicate content detected$/i, "This looks very similar to something you posted recently. Please share something new."],
  [/^repeated characters detected$/i, "Please avoid repeating the same character many times in a row."],
  [/^too many links$/i, "Your post contains too many links. Please remove some and try again."],
  [/^cooldown active\. wait (\d+) minutes?\.?$/i, (m) => `You're posting too quickly. Please wait ${m[1]} more minute${m[1] === "1" ? "" : "s"} before trying again.`],
  [/^daily limit reached \((\d+)\)$/i, (m) => `You've reached today's limit of ${m[1]} posts. Please come back tomorrow.`],
];

// Strips the "(path -> status)" debug suffix added by formatApiError and
// swaps in a friendlier sentence for known backend messages, so regular
// users see plain explanations instead of raw codes like 400/401.
export function humanizeApiError(error) {
  const raw = `${error?.message ?? ""}`.replace(/\s*\([^)]*->[^)]*\)\s*$/, "").trim();
  for (const [pattern, replacement] of FRIENDLY_MESSAGES) {
    const match = raw.match(pattern);
    if (match) {
      return typeof replacement === "function" ? replacement(match) : replacement;
    }
  }
  return raw || "Something went wrong. Please try again.";
}

function formatApiError(err, path) {
  const status = err.response?.status;
  const body = err.response?.data;
  // body.message holds the specific reason. It's usually a single string
  // (e.g. "Cooldown active. Wait 60 minutes." from AntiFarmingService), but
  // NestJS's global ValidationPipe returns an ARRAY of strings for DTO-level
  // validation failures (e.g. ["categoryId must be a UUID"]) — join those
  // into one readable line instead of letting them stringify into "a,b,c".
  // body.error is just the generic HTTP reason phrase (e.g. "Bad Request")
  // and must be checked AFTER message, or the useful text gets discarded.
  const bodyMessage = Array.isArray(body?.message) ? body.message.join(". ") : body?.message;
  const message =
    bodyMessage || body?.error || err.message || `API error ${status ?? "unknown"}`;
  const error = new Error(`${message} (${path} -> ${status ?? "unknown"})`);
  error.status = status;
  error.path = path;
  console.error("API request failed:", { path, status, message });
  return error;
}

export async function request(path, options = {}) {
  if (!BASE_URL) {
    throw new Error("API not configured: set VITE_API_BASE_URL in your .env file");
  }
  try {
    const res = await apiClient({
      url: path,
      method: options.method ?? "GET",
      data: parseBody(options.body),
      headers: {
        ...(options.cache === "no-store" ? { "Cache-Control": "no-cache" } : {}),
        ...options.headers,
      },
    });
    if (res.status === 204) return null;
    return res.data ?? null;
  } catch (err) {
    throw formatApiError(err, path);
  }
}

export async function adminRequest(path, options = {}) {
  if (!BASE_URL) {
    throw new Error("API not configured: set VITE_API_BASE_URL in your .env file");
  }
  try {
    const res = await apiClient({
      url: path,
      method: options.method ?? "GET",
      data: parseBody(options.body),
      headers: {
        // Admin GETs were being served a stale cached body by the browser
        // (visible as a 304 in devtools) whose shape no longer matched the
        // current API response, which crashed list screens like User
        // Management ("s.map is not a function"). Force no-cache so admin
        // requests always hit the network for a fresh, correctly-shaped body.
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        ...options.headers,
      },
    });
    return res.data;
  } catch (err) {
    const body = err.response?.data;
    const status = err.response?.status;
    const bodyMessage = Array.isArray(body?.message) ? body.message.join(". ") : body?.message;
    const message =
      bodyMessage ||
      body?.error ||
      err.message ||
      `API error ${status ?? "unknown"}`;
    const error = new Error(message);
    error.status = status;
    throw error;
  }
}
